import React from "react";
import { waitForEvent, fetchCart, throttle } from 'static.theme.js'
import { cartNotesQuery } from 'static.graphql-fragments.js'
import { scroll, animate } from 'static.vendor.min.js'
import { spring } from 'motion'
import { client } from "@utility/graphql";
import { createRoot } from 'react-dom/client';
import { Engine } from 'json-rules-engine';
import PDPDealer from "@components/dealer-locator/PDPDealer.jsx";

import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer'
export class ScrollButton extends HTMLElement {
    constructor() {
        super();
        this.href = this.getAttribute('href');
    }
    connectedCallback() {
        this.addEventListener('click', (event) => {
            event.preventDefault();
            const target = document.getElementById(this.href.replace('#', ''));
            if(target){
                target.scrollIntoView({behavior: 'smooth'});
            }
        });
    }
}


export class ProductToolList extends HTMLElement {
    constructor() {
        super();
    }
    connectedCallback() {
        this.carousel = this.querySelector("scroll-carousel");
        this.drawer = this.querySelector("x-drawer");
        const items = this.querySelectorAll(".product-tool-list__item");

        items.forEach((item, index) => {
            item.addEventListener('click', async () => {
                await waitForEvent(this.drawer, "dialog:after-show");
                this.carousel.select(index);
            });
        });

    }
}


export class EmployeeList extends HTMLElement {
    constructor() {
        super();
    }
    connectedCallback() {
        this.template = this.querySelector("#employee-item-template");
        this.dataEl = this.querySelector("#employee-data");
        this.data = JSON.parse(this.dataEl.innerHTML);
        this.randomize = this.dataset.randomize
        this.limit = this.dataset.limit || 0

        this.render();
    }

    shuffle(array) {
        let currentIndex = array.length, randomIndex;
        while (currentIndex != 0) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;
            [array[currentIndex], array[randomIndex]] = [
                array[randomIndex], array[currentIndex]];
        }
        return array;
    }

    render(){
        const list = this.querySelector(".employee-list__list");
        list.innerHTML = "";
        const employees = this.randomize ? this.shuffle(this.data) : this.data;
        const limitedEmployees = parseInt(this.limit) > 0 ? employees.slice(0, this.limit) : employees;
        limitedEmployees.forEach((employee) => {
            list.appendChild(this.renderItem(employee));
        });
    }

    renderItem(employee){
        const clone = this.template.content.cloneNode(true);
        const name = clone.querySelector(".employee-card__name");
        const title = clone.querySelector(".employee-card__title");
        const image = clone.querySelector(".employee-card__image");
        const bio = clone.querySelector(".employee-card__bio");

        name.textContent = employee.name;
        title.textContent = employee.title;
        image.innerHTML = employee.photo;
        bio.insertAdjacentHTML('beforeend', employee.bio);

        return clone;
    }
}


export class ImageBannerScrollOverlay extends HTMLElement {
    constructor() {
        super();
        this.boundIntro = this.intro.bind(this);
        this.boundAttachScroll = this.attachScroll.bind(this);
        this.startingPosition = this.getAttribute('starting-position');
        this.endingPosition = this.getAttribute('ending-position');
        if(!this.startingPosition || isNaN(this.startingPosition)){
            this.startingPosition = 100;
        }
        if(!this.endingPosition || isNaN(this.endingPosition)){
            this.endingPosition = 0;
        }
    }
    connectedCallback() {

        window.setTimeout(() => {
            const imageRect = this.getBoundingClientRect();
            const done = ( window.innerHeight * this.startingPosition / 100 ) - imageRect.top;
            const left = imageRect.bottom - ( window.innerHeight * this.endingPosition / 100 );
            const progress = done / (done + left);
            this.boundIntro(progress);
        }, 1000);

    }

    intro(progress){
        const percentage = ((1 - progress) * 100).toFixed(2)
        animate( this, {
            clipPath: ["inset(0 0 0 100%)", `inset(0 0 0 ${percentage}%)`],
        },{
            easing: spring({ damping: 15 }),
        }).finished
        .then(this.boundAttachScroll);
    }

    attachScroll(){
        scroll(
            animate(this, {
                clipPath: ["inset(0 0 0 100%)", "inset(0 0 0 0)"],
            }),
            {
                target: this,
                easing: "linear",
                offset: [`start ${this.startingPosition}%`, `end ${this.endingPosition}%`]
            }
        );
    }
}

export class LottieOverlay extends HTMLElement {
    constructor() {
        super();
        this.player = this.querySelector("lottie-player");
        this.progress = 0;
        this.boundIntro = this.intro.bind(this);
        this.boundAttachScroll = this.attachScroll.bind(this);
        this.startingPosition = this.getAttribute('starting-position');
        this.endingPosition = this.getAttribute('ending-position');
        if(!this.startingPosition || isNaN(this.startingPosition)){
            this.startingPosition = 100;
        }
        if(!this.endingPosition || isNaN(this.endingPosition)){
            this.endingPosition = 0;
        }
    }

    connectedCallback() {
        this.player.addEventListener("ready", () => {

            console.log("You've captured the ready event!");

            window.setTimeout(() => {
                const imageRect = this.getBoundingClientRect();
                const done = ( window.innerHeight * this.startingPosition / 100 ) - imageRect.top;
                const left = imageRect.bottom - ( window.innerHeight * this.endingPosition / 100 );
                const progress = done / (done + left);
                this.boundIntro(progress);
            }, 500);

        });

    }

    intro(progress){
        //console.log(progress);
        this.player.addEventListener("frame", (event) => {
            if(event.detail.seeker > progress * 100){
                this.player.pause();
                this.progress = progress.toFixed(2);
                this.boundAttachScroll();
            }
        });
        this.player.play();
    }

}

export class ScrollAnimation extends HTMLElement {
    constructor() {
        super();
        this.boundAttachScroll = this.attachScroll.bind(this);
        this.animation = this.getAttribute('animation');
    }
    connectedCallback() {
        this.boundAttachScroll();
    }

    attachScroll(){
        switch (this.animation){
            case "zoom-in-out":
                scroll(
                    animate(this, {
                        transform: ["scale(0.7)", "scale(1)", "scale(0.7)"]
                    }),
                    {
                        target: this,
                        easing: ["ease-in", "ease-out"],
                        offset: [`start end`, `end start`]
                    }
                );
                break;
            case "zoom-in":
                scroll(
                    animate(this, {
                        transform: ["scale(0.7)", "scale(1)"]
                    }),
                    {
                        target: this,
                        easing: "ease-in",
                        offset: [`start end`, `end end`]
                    }
                );
                break;
        }
    }
}

export class AssociatedVariants extends HTMLElement {
    constructor() {
        super();
        this.swatches = this.querySelectorAll(".swatch__container");
        this.inputs = this.querySelectorAll(".swatch__container input");
        this.selectedVariant = null;
        this.productFormId = this.dataset.productFormId;
        this.autoSelect = this.hasAttribute("auto-select");
    }
    connectedCallback() {
        this.formNode = document.querySelector("#" + this.productFormId);

        window.addEventListener('vehiclePickerReset', (event) => {
            this.setAttribute('hidden', 'hidden');
        });
        window.addEventListener('vehiclePickerOpened', (event) => {
            this.setAttribute('hidden', 'hidden');
        });

        document.addEventListener('variant:change', (event) => {
            this.removeAttribute('hidden');
            this.update(event.detail.variant);
        });

        document.addEventListener('vehicleSelected', (e) => {
            this.removeAttribute('hidden');
            const variants = window.__VPV.pdp.remainingVariants;
            if(variants?.length){
                this.update(variants[0]);
            }
        });

        this.inputs.forEach((input) => {
            input.addEventListener('change', (event) => {
                if(this.formNode){
                    this.setAdditionalVariant(event.target.value);
                }else{
                    console.error("No form node found for adding additional variant.");
                }
            });
        });

        document.addEventListener('vehicleVarsDataReady', (e) => {
            this.update(e.detail[0]);
        });

        this.update();
    }

    setAdditionalVariant(value){
        if(value == this.selectedVariant) return;
        this.selectedVariant = value;
        var requiredVariant = this.formNode.querySelector('input[name="required_variant"]');
        if (!requiredVariant) {
          requiredVariant = document.createElement("input");
          requiredVariant.setAttribute("type", "hidden");
          requiredVariant.setAttribute("name", "required_variant");
          this.formNode.prepend(requiredVariant);
        }
        var requiredVariantNote = this.formNode.querySelector('input[name="required_variant_note"]');
        if(!requiredVariantNote){
          requiredVariantNote = document.createElement("input");
          requiredVariantNote.setAttribute("type", "hidden");
          requiredVariantNote.setAttribute("name", "required_variant_note");
          this.formNode.prepend(requiredVariantNote);
        }
        if(value){
          requiredVariant.value = value;
        }else{
          requiredVariant.value = null;
          requiredVariantNote.value = null;
        }
    }
    update(variant){
        if(!variant){
            const variants = window.__VPV.pdp.remainingVariants;
            if(variants?.length){
                variant = variants[0];
            }else{
                return;
            }
        }
        const compatibility = variant.variantParentCompatibility?.find(({key}) => key === "parent_keys");
        if(compatibility){
            try{
                const compatibilityKeys = JSON.parse(compatibility.value);
                if(compatibilityKeys && compatibilityKeys.length > 0){
                    var hideContainer = true;
                    this.swatches.forEach((swatch) => {
                        const swatchKeys = swatch.dataset.compatibility ? JSON.parse(swatch.dataset.compatibility) : [];

                        if(swatchKeys && swatchKeys.length > 0){
                            var hidden = false;
                            if (compatibilityKeys.length > 0 && swatchKeys.length > 0){
                                hidden = true;
                                compatibilityKeys.forEach((key) => {
                                    if(swatchKeys.includes(key)){
                                        hidden = false;
                                    }
                                });
                            }
                            const input = swatch.querySelector("input");
                            if(hidden){
                                if (input.checked){
                                    this.setAdditionalVariant(null);
                                }
                                input.setAttribute("disabled", "disabled");
                            }else{
                                input.removeAttribute("disabled");
                                hideContainer = false;
                            }
                            swatch.classList.toggle("hidden", hidden);
                        }else{

                            swatch.classList.toggle("hidden", false);
                            hideContainer = false;
                        }
                    });
                    this.classList.toggle("hidden", hideContainer);
                }
            }catch(e){
                console.error("Error parsing compatibility keys", e);
            }
        }

        if(this.autoSelect){
            const selected = this.querySelectorAll("input:not([disabled])")[0];
            if(selected){
                selected.checked = true;
                this.setAdditionalVariant(selected.value);
            }
        }
    }
}

import { CanvasImgSequence } from "canvas-img-sequence";
import { SpringFollower } from "../utility/animations";

export class AnimatedCanvas extends HTMLElement {
    constructor(){
        super();
        this.length = this.getAttribute("length");
        this.width = this.getAttribute("width");
        this.height = this.getAttribute("height");
        this.src = [];
        const baseUrl = this.getAttribute("base-url");
        const fileName = baseUrl.split('/').pop()
        if(fileName){
            const numberMatches = fileName.match(/(\d+)(?=\.)/g);
            if(numberMatches){
                const firstNumber = parseInt(numberMatches[0]);
                const lastNumber = firstNumber + parseInt(this.length);
                for(var i = firstNumber; i < lastNumber; i++){
                    const src = baseUrl.replace(firstNumber, i);
                    this.src.push(src);
                }
            }
        }
    }
    connectedCallback(){
        if(this.src.length < 1){
            console.error("No image sequence found.");
            return;
        }

        this.imgSequence = new CanvasImgSequence(this, {
                width: this.width,
                height: this.height,
                observer: true,
                images: this.src,
        });
    }
}

var fetchNotes = new Promise((resolve, reject) => {
    window.__VPV = window.__VPV || {};
    if(window.__VPV.cartNotes){
        resolve(window.__VPV.cartNotes);
    }
    client.request(cartNotesQuery, {}).then((notes) => {
        window.__VPV.cartNotes = notes.cartNotes.edges;
        resolve(notes.cartNotes.edges);
    }).catch((e) => {
        console.error("Error fetching cart notes", e);
        reject(e);
    })
});

export class CartNote extends HTMLElement {
    constructor() {
        super();
        this.engine = new Engine();

        fetchNotes.then((notes) => {
            this.notes = notes;
            this.render();
        });
        if(fetchCart.items){
            this.cart = fetchCart;
            this.render();
        }else{
            fetchCart.then((cart) => {
                this.cart = cart;
                this.render();
            });
        }
    }
    connectedCallback() {
        try{
            this.facts = JSON.parse(this.dataset.facts);
            this.render();
        }catch(e){
            console.error("Error parsing facts", e);
        }
    }
    render(){

        if(this.cart && this.facts && this.notes){

            let filteredCartNotes = this.notes;

            if(process.env.NODE_ENV != 'development' && !location.search.includes("debug")){
              filteredCartNotes = filteredCartNotes.filter(({node: note}) => note.active?.value == 'true');
            }

            filteredCartNotes?.forEach(({node: note}) => {
              try{
                this.engine.addRule({
                  conditions: JSON.parse(note.rules?.value),
                  event: {
                    type: 'note',
                    params: {
                      content: note.content?.value,
                      priority: note.priority?.value,
                      level: note.level?.value
                    }
                  }
                });
              }catch(e){
                console.error(`Issue with cart note: ${note.name.value}`);
                console.error(e);
              }
            });

            const facts = {
                ...this.facts,
                cart_skus: this.cart.items.map((item) => item.sku),
                cart_handles: this.cart.items.map((item) => item.handle)
            }

            if(location.search.includes("debug")){
              console.log("======== DEBUGGING CART NOTE RULES - CART DRAWER ========");
              console.log("Facts:", facts);
              console.log("Notes:", filteredCartNotes);
            }

            this.engine.run(facts).then(({ events }) => {
                events = events.sort((a, b) => {
                    return a.params.priority - b.params.priority;
                });
                const content = events[0]?.params.content;
                if(!content) return;

                this.innerHTML = "";
                const noteEl = document.createElement("div");
                noteEl.classList.add("line-item__note", `line-item__note--${events[0]?.params.level || 'info'}`);
                this.content = convertSchemaToHtml(content);
                noteEl.innerHTML = this.content;
                this.appendChild(noteEl);
            });

        }
    }
}

export class SeeItInPerson extends HTMLElement {
    constructor(){
      super();
    }
    connectedCallback() {
      try{
        this.filtersEl = this.querySelector('.filters');
        this.filters = JSON.parse(this.filtersEl.innerHTML);
      }catch(e){
        console.error(e);
      }
      this.container = document.createElement('div');
      this.appendChild(this.container);
      this.show();
    }
    show(){
      this.root = createRoot(this.container);
      this.root.render(<PDPDealer filters={this.filters} radius={this.dataset.radius} apiKey={this.dataset.apiKey} />);
    }
}
export class ProductVariations extends HTMLElement {
    constructor(){
        super();
        this.boundOnVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
        this.swatches = this.querySelectorAll(".swatch__container");
        window.addEventListener('variant:change', this.boundOnVariantChange);
    }

    onVariantChange(event){
        const handle = event.detail.variant?.product?.handle;
        const swatch = this.querySelector(`.swatch__container[data-handle="${handle}"]`);
        if(swatch){
            this.selectSwatch(swatch);
        }
    }

    selectSwatch(swatch){
        this.swatches.forEach((swatch) => {
            swatch.classList.remove("selected_swatch");
        });
        swatch.classList.add("selected_swatch");
    }
}

export class SystemCarouselItem extends HTMLElement {
    constructor() {
        super();
        this.carousel = this.closest("scroll-carousel");
        this.animation = this.querySelector('animated-canvas');
        const initialIndex = this.carousel?.getAttribute("initial-index") || 1;
        this.animationInitialized = this.dataset.index != initialIndex;
        this.opacity = 0;
        this.transform = "translateX(40px)";
        if(this.animation){
            this.springFollower = new SpringFollower(this.animation, "frame", { damping: 50, stiffness: 200, mass: 5 });
            this.manualTarget = this.animation.getAttribute("pause-frame") == null ? null : Math.round(this.animation.getAttribute("pause-frame"));
            if(this.manualTarget !== null){
                this.currentTarget = this.manualTarget;
                this.animation.addEventListener("click", (e) => {
                    e.preventDefault();
                    this.springFollower.setTarget(this.currentTarget);
                    this.currentTarget = this.manualTarget === this.currentTarget ? this.animationLength : this.manualTarget;
                });
            }
        }
    }
    connectedCallback() {
        this._getSizes();
        this._updateScrollProgress();
        this.carousel.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));
        if (window.ResizeObserver) {
            new ResizeObserver(()=>{
                this._getSizes();
                this._updateScrollProgress.bind(this)
            }).observe(this.carousel);
        }
        if (window.IntersectionObserver) {
            new IntersectionObserver((entries)=>{
                entries.forEach((entry) => {
                    if(entry.isIntersecting && !this.animationInitialized){
                        this.animationInitialized = true;
                        this._updateScrollProgress();
                    }
                });
            }, {threshold: [.5]}).observe(this);
        }
        this.addEventListener("click", (e) => {
            if(!this.active){
                e.preventDefault();
                this.carousel.select(this.dataset.index);
            }
        });
    }
    _getSizes() {
        const carouselStyles = window.getComputedStyle(this.carousel);
        this.carouselGap = parseFloat(carouselStyles.columnGap) || 0;
        this.carouselPaddingLeft = parseFloat(carouselStyles.paddingLeft) || 0;
        this.width = this.offsetWidth;
        this.left = this.offsetLeft - this.carouselPaddingLeft;
        if(this.animation){
            this.animationLength = this.animation.getAttribute("length");
        }
    }
    _updateScrollProgress() {
        const itemWidth = this.width + this.carouselGap;
        const offsetLeft = this.left;
        const scrollLeft = this.carousel.scrollLeft;
        //const progress = Math.max(2 * (.5 - Math.abs((scrollLeft - offsetLeft) / itemWidth)), 0);
        const progress = Math.max(1 - Math.abs((scrollLeft - offsetLeft) / itemWidth), 0);
        const frame = this.animationLength ? Math.abs(Math.round(progress * this.animationLength)) : 0;

        if(this.animation && this.animationInitialized){
            this.springFollower.setTarget(frame);
        }

        if(progress > .5){
            this.active = true;
            this.classList.remove("inactive");
        }else{
            this.active = false;
            this.classList.add("inactive");
        }
        this.style.setProperty("--active", progress > .5 ? 1 : 0);
        this.style.setProperty("--opacity", Math.max(.5, 1 - (.5 * Math.abs((scrollLeft - offsetLeft) / itemWidth))));
        //this.style.setProperty("--scale", Math.max(.8, 1 - (.2 * Math.abs((scrollLeft - offsetLeft) / itemWidth))));
    }
}


export class AjaxForm extends HTMLElement {
    constructor() {
        super();
    }
    connectedCallback() {
        this.form = this.querySelector("form");
        if(this.form){
            this.form.addEventListener("submit", this.onSubmit.bind(this));
        }
    }

    displayError(message){
        this.form.insertAdjacentHTML('beforeend', `<div class="banner banner--error">${message}</div>`);
    }

    displaySuccess(message){
        this.form.insertAdjacentHTML('beforeend', `<div class="banner banner--success">${message}</div>`);
    }

    // async onSubmit(event){
    //     this.querySelectorAll(".banner").forEach((banner) => banner.remove());
    //     event.preventDefault();
    //     const formData = new FormData(this.form);
    //     const action = this.form.getAttribute("action") || window.location.href;
    //     try{
    //         const response = await fetch(action, {
    //             method: "POST",
    //             body: formData,
    //             headers: {
    //                 'Accept': 'application/json',
    //             },
    //         });
    //         if(response.ok){
    //             const data = await response.json();
    //             this.dispatchEvent(new CustomEvent('form:success', { detail: data }));
    //             this.displaySuccess("Your form has been submitted successfully.");
    //         }else{
    //             const errorData = await response.json();
    //             this.dispatchEvent(new CustomEvent('form:error', { detail: errorData }));
    //             this.displayError("There was an error submitting the form. Please try again.");
    //         }
    //     }catch(e){
    //         console.error("Error submitting form", e);
    //         this.dispatchEvent(new CustomEvent('form:error', { detail: e }));
    //         this.displayError("There was an error submitting the form. Please try again.");
    //     }
    //     this.querySelectorAll("[type='submit']").forEach((button) => button.removeAttribute("aria-busy"));
    // }
}

export class ShowIfUrl extends HTMLElement {
    connectedCallback() {
        const pattern = this.getAttribute('match');
        if (pattern && !window.location.href.includes(pattern)) {
            this.style.display = 'none';
        }
    }
}

export class KustomerForm extends HTMLElement {
    constructor() {
        super();
    }
    connectedCallback() {
        this.src = this.getAttribute("src");
        this.width = this.getAttribute("width") || "100%";
        //subscribe to height messages
        window.addEventListener("message", (event) => {
            if(event.origin.includes("decked.kustomer.help") || event.origin.includes("help.decked.com")){
                if(event.source != this.iframe.contentWindow) return;

                try{
                    const data = event.data;
                    console.log("Kustomer message received", data);
                    if(data.type === "resize_iframe" && data.height){
                        this.style.height = (10 + data.height) + "px";
                    }else if(data.type === "form_submission"){
                        console.log("Kustomer form submission", data.payload);
                        window.dispatchEvent(new CustomEvent('kustomer:form-submission', { detail: data.payload }));
                    }
                }catch(e){
                    //not JSON, ignore
                }
            }
        });
        console.log("Kustomer Form src:", this.src);
        this.render();
    }
    render(){
        this.innerHTML =
        `<div class="kustomer-form-skeleton v-stack gap-3 w-full max-w-md m-auto p-4">
            <div class="h-stack gap-3 w-full">
                <div class="w-full v-stack gap-3">
                    <span class="skeleton skeleton--text" style="--skeleton-text-width: 20%"></span>
                    <span class="skeleton skeleton--input mb-3" style="--skeleton-text-width: 100%"></span>
                </div>
                <div class="w-full v-stack gap-3" style="margin-top:0 !important">
                    <span class="skeleton skeleton--text" style="--skeleton-text-width: 20%"></span>
                    <span class="skeleton skeleton--input mb-3" style="--skeleton-text-width: 100%"></span>
                </div>
            </div>
            <span class="skeleton skeleton--text" style="--skeleton-text-width: 20%"></span>
            <span class="skeleton skeleton--input mb-3" style="--skeleton-text-width: 100%"></span>
            <span class="skeleton skeleton--text" style="--skeleton-text-width: 22%"></span>
            <span class="skeleton skeleton--input mb-3" style="--skeleton-text-width: 100%"></span>
            <span class="skeleton skeleton--text" style="--skeleton-text-width: 25%"></span>
            <span class="skeleton skeleton--input mb-3" style="--skeleton-text-width: 100%"></span>
            <span class="skeleton skeleton--text" style="--skeleton-text-width: 30%"></span>
            <span class="skeleton skeleton--input" style="--skeleton-text-width: 10%"></span>
            <span class="skeleton skeleton--input mb-3" style="--skeleton-text-width: 10%"></span>
            <span class="skeleton skeleton--text" style="--skeleton-text-width: 30%"></span>
            <span class="skeleton skeleton--input mb-3" style="--skeleton-text-width: 100%"></span>
        </div>`
        this.iframe = document.createElement("iframe");
        this.iframe.setAttribute("src", this.src);
        this.iframe.setAttribute("frameborder", "0");
        // this.iframe.setAttribute("scrolling", "no");
        this.iframe.style.border = "none";
        this.iframe.style.overflow = "hidden";
        this.iframe.style.width = this.width;
        this.iframe.style.height = "100%";
        this.appendChild(this.iframe);

        // once iframe loads, remove skeleton
        this.iframe.addEventListener("load", () => {
            this.classList.add("kustomer-form--loaded");
        });
    }
}



export class GiftCardRecipientForm extends HTMLElement {
    connectedCallback() {
        this.details = this.querySelector('details');
        this.recipientToggle = this.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"]');
        this.recipientEmail = this.querySelector('[name="properties[Recipient email]"]');
        this.recipientName = this.querySelector('[name="properties[Recipient name]"]');
        this.message = this.querySelector('[name="properties[Message]"]');
        this.sendOn = this.querySelector('[name="properties[Send on]"]');
        if (!this.details || !this.recipientToggle) return;

        if (this.sendOn && !this.sendOn.value) {
            const today = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            this.sendOn.value = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
        }

        this.details.addEventListener('toggle', this.onToggle.bind(this));
        this.onToggle();
    }
    onToggle() {
        const isOpen = this.details.open;
        this.recipientToggle.disabled = !isOpen;
        [this.recipientEmail, this.recipientName, this.message].forEach((field) => {
            if (!field) return;
            field.required = isOpen;
        });
    }

    /**
     * Called by ProductForm on submit to find out which (if any) gift card recipient
     * properties should be sent to cart/add.js, and to validate them.
     *
     * @returns {null|false|Object} null if not sending to a recipient (caller should
     *   strip those properties), false if sending to a recipient but the fields are
     *   invalid (caller should abort, native validation UI is shown here), or an
     *   object of form-encoded property keys/values to merge into the submission.
     */
    getRecipientProperties() {
        if (!this.recipientToggle || this.recipientToggle.disabled) {
            return null;
        }

        const requiredFields = [this.recipientEmail, this.recipientName, this.message].filter(Boolean);
        const allValid = requiredFields.every((field) => field.reportValidity());
        if (!allValid) {
            return false;
        }

        return {
            'properties[Recipient email]': this.recipientEmail.value,
            'properties[Recipient name]': this.recipientName.value,
            'properties[Message]': this.message.value,
            'properties[Send on]': this.sendOn?.value ?? '',
        };
    }
}

export class ShowcaseImageScroll extends HTMLElement {
  constructor() {
    super();
    this.currentItem = 1;
  }

  connectedCallback() {
    this.observedElement.addEventListener('carousel:select', this._updateProgress.bind(this));
    this.observedElement.addEventListener('carousel:settle', this._updateProgress.bind(this));
    document.addEventListener('scroll', this._updateProgressScreen.bind(this));

    const el = this.querySelector(`[data-index="${this.currentItem}"]`);
    el.style.setProperty('display', 'block');
    this.products = this.observedElement.querySelectorAll('.product-card');
    this.products?.[0]?.classList.add('active');
  }

  get observedElement() {
    return (this._observedElement = this._observedElement || document.getElementById(this.getAttribute('observes')));
  }

  _updateProgressScreen(e) {
    if (window.innerWidth < 1150) return;
    const rect = this.observedElement.getBoundingClientRect();
    const hiddenTop = Math.max(0, -rect.top);
    const hiddentTopPercent = hiddenTop / this.observedElement.scrollHeight;
    const itemHeight = this.observedElement.scrollHeight / this.products.length;
    let indexSelected;
    if (hiddenTop <= 0) {
      indexSelected = 1;
    } else {
      indexSelected = Math.ceil(hiddenTop / itemHeight) + 1;
    }
    if (indexSelected != this.currentItem && indexSelected <= this.products.length) {
      this.currentItem = indexSelected;
      this.querySelectorAll(`.showcase__image_scroll_block`).forEach(el => {
        el.style.display = 'none';
      });
      this.querySelector(`[data-index="${indexSelected}"]`).style.setProperty('display', 'block');

      this._updateActiveClass(indexSelected - 1);
    }
  }

  _updateProgress(e) {
    if (window.innerWidth > 1149) return;
    requestAnimationFrame(() => {
      if (this.currentItem != e.detail.index + 1) {
        this.currentItem = e.detail.index + 1;
        this.querySelectorAll(`.showcase__image_scroll_block`).forEach(el => {
          el.style.display = 'none';
        });
        this.querySelector(`[data-index="${e.detail.index + 1}"]`).style.setProperty('display', 'block');
        this._updateActiveClass(e.detail.index);
      }
    });
  }

  _updateActiveClass(newIndex) {
    this.products.forEach((el, index) => {
      if (index == newIndex) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });
  }
}
